import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Stack,
  Text,
  Badge,
  Button,
  Spinner,
  HStack,
} from "@chakra-ui/react";
import { FiBell } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  fetchNotifications,
  fetchUnreadCount,
  markRead,
  markAllRead,
  linkForNotification,
} from "../services/notificationsService";

const POLL_MS = 30000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const { count: c } = await fetchUnreadCount();
      setCount(c || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const loadDropdown = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchNotifications({ limit: 10 });
      setItems(data.notifications || []);
      setCount(data.unread_count || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  useEffect(() => {
    if (open) loadDropdown();
  }, [open, loadDropdown]);

  const onClickItem = async (n) => {
    if (!n.is_read) {
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
      setCount((c) => Math.max(0, c - 1));
      try {
        await markRead(n.id);
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    const link = linkForNotification(n);
    if (link && link !== "/notifications") {
      navigate(link);
    }
  };

  const onMarkAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setCount(0);
    try {
      await markAllRead();
    } catch {
      refreshCount();
    }
  };

  return (
    <Popover
      isOpen={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement="bottom-end"
    >
      <PopoverTrigger>
        <Box position="relative" display="inline-block">
          <IconButton
            aria-label="Notifications"
            icon={<FiBell />}
            variant="ghost"
            size="md"
          />
          {count > 0 && (
            <Badge
              position="absolute"
              top="-3px"
              right="-3px"
              bg="gwbi.600"
              color="white"
              border="2px solid"
              borderColor="surface.card"
              borderRadius="full"
              fontSize="0.62em"
              px={1.5}
              minW="18px"
              lineHeight="1.15"
              textAlign="center"
              boxShadow="sm"
            >
              {count > 99 ? "99+" : count}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="360px" zIndex={1500}>
        <PopoverHeader fontWeight="semibold">
          <HStack justify="space-between">
            <Text>Notifications</Text>
            {count > 0 && (
              <Button size="xs" variant="link" onClick={onMarkAll}>
                Mark all read
              </Button>
            )}
          </HStack>
        </PopoverHeader>
        <PopoverBody maxH="400px" overflowY="auto" p={0}>
          {loading ? (
            <Box p={4} textAlign="center">
              <Spinner size="sm" />
            </Box>
          ) : items.length === 0 ? (
            <Box p={4}>
              <Text fontSize="sm" color="text.muted">
                No notifications yet.
              </Text>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Box borderTop="1px solid" borderColor="border.default" />}>
              {items.map((n) => {
                const link = linkForNotification(n);
                const hasLink = link && link !== "/notifications";
                return (
                  <Box
                    key={n.id}
                    px={4}
                    py={3}
                    cursor="pointer"
                    bg={n.is_read ? "transparent" : "blue.50"}
                    _hover={{ bg: "gray.100" }}
                    onClick={() => onClickItem(n)}
                  >
                    <HStack justify="space-between" align="start">
                      <Box flex={1}>
                        <Text fontSize="sm" fontWeight={n.is_read ? "normal" : "semibold"}>
                          {n.message || n.type}
                        </Text>
                        <Text fontSize="xs" color="text.muted" mt={1}>
                          {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                        </Text>
                      </Box>
                      {!n.is_read && (
                        <Badge colorScheme="blue" fontSize="0.6em" mt={0.5}>
                          new
                        </Badge>
                      )}
                    </HStack>
                    {hasLink && (
                      <Text fontSize="xs" color="gwbi.500" mt={1} fontWeight="medium">
                        {link.replace(/^\//, "").split("/")[0].replace(/-/g, " ")} →
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </PopoverBody>
        <PopoverFooter>
          <Button
            size="sm"
            variant="ghost"
            w="100%"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
          >
            View all
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
